import { create } from 'zustand';
import { toast } from 'sonner';
import { devtools, persist } from 'zustand/middleware';

type NotificationType = 'success' | 'warning' | 'error' | 'info';
type ModalType = 'confirm' | 'alert' | 'custom';
export type ColorMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveColorMode(mode: ColorMode): ResolvedTheme {
  return mode === 'system' ? getSystemTheme() : mode;
}

export function applyDocumentTheme(mode: ColorMode): ResolvedTheme {
  const resolved = resolveColorMode(mode);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  return resolved;
}

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
  actions?: Array<{
    label: string;
    action: () => void;
    primary?: boolean;
  }>;
  timestamp: number;
}

function pushSonnerToast(notification: Notification) {
  const duration = notification.persistent ? Infinity : notification.duration || 5000;
  const primaryAction = notification.actions?.find((action) => action.primary) ?? notification.actions?.[0];
  const options = {
    id: notification.id,
    description: notification.message,
    duration,
    action: primaryAction
      ? {
          label: primaryAction.label,
          onClick: () => primaryAction.action(),
        }
      : undefined,
  };

  switch (notification.type) {
    case 'success':
      toast.success(notification.title, options);
      break;
    case 'warning':
      toast.warning(notification.title, options);
      break;
    case 'error':
      toast.error(notification.title, options);
      break;
    default:
      toast(notification.title, options);
      break;
  }
}

interface Modal {
  id: string;
  type: ModalType;
  title: string;
  content: string | React.ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closable?: boolean;
}

interface LoadingState {
  id: string;
  message: string;
  progress?: number;
}

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  notifications: Notification[];
  modals: Modal[];
  loadingStates: LoadingState[];
  isFullscreen: boolean;
  keyboardShortcutsEnabled: boolean;
  animationsEnabled: boolean;
  soundEnabled: boolean;
  compactMode: boolean;
  fontSize: 'small' | 'medium' | 'large';
  language: string;
  colorMode: ColorMode;
  resolvedTheme: ResolvedTheme;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  showModal: (modal: Omit<Modal, 'id'>) => string;
  hideModal: (id: string) => void;
  hideAllModals: () => void;
  startLoading: (message: string, id?: string) => string;
  updateLoadingProgress: (id: string, progress: number, message?: string) => void;
  stopLoading: (id: string) => void;
  stopAllLoading: () => void;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  toggleFullscreen: () => void;
  setKeyboardShortcuts: (enabled: boolean) => void;
  setAnimations: (enabled: boolean) => void;
  setSound: (enabled: boolean) => void;
  setCompactMode: (enabled: boolean) => void;
  setFontSize: (size: 'small' | 'medium' | 'large') => void;
  setLanguage: (language: string) => void;
  setColorMode: (mode: ColorMode) => void;
  syncResolvedTheme: () => void;
  showSuccessNotification: (title: string, message: string) => void;
  showErrorNotification: (title: string, message: string) => void;
  showWarningNotification: (title: string, message: string) => void;
  showInfoNotification: (title: string, message: string) => void;
  showConfirmModal: (title: string, content: string, onConfirm: () => void) => string;
  showNotification: (notification: { type: NotificationType; title: string; message: string; duration?: number }) => string;
}

type UIStore = UIState & UIActions;

// Auto-remove notification timers
const notificationTimers = new Map<string, NodeJS.Timeout>();

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        sidebarOpen: true,
        sidebarCollapsed: false,
        notifications: [],
        modals: [],
        loadingStates: [],
        isFullscreen: false,
        keyboardShortcutsEnabled: true,
        animationsEnabled: true,
        soundEnabled: true,
        compactMode: false,
        fontSize: 'medium',
        language: 'en',
        colorMode: 'system',
        resolvedTheme: 'light',

        // Actions
        toggleSidebar: () => {
          set(state => ({ sidebarOpen: !state.sidebarOpen }));
        },

        setSidebarOpen: (open: boolean) => {
          set({ sidebarOpen: open });
        },

        toggleSidebarCollapse: () => {
          set(state => ({ sidebarCollapsed: !state.sidebarCollapsed }));
        },

        setSidebarCollapsed: (collapsed: boolean) => {
          set({ sidebarCollapsed: collapsed });
        },

        addNotification: (notification) => {
          const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const newNotification: Notification = {
            ...notification,
            id,
            timestamp: Date.now(),
          };

          // Keep a light in-memory list for Escape-to-dismiss shortcuts
          set(state => ({
            notifications: [...state.notifications, newNotification],
          }));

          pushSonnerToast(newNotification);

          if (!notification.persistent) {
            const duration = notification.duration || 5000;
            const timer = setTimeout(() => {
              get().removeNotification(id);
            }, duration);

            notificationTimers.set(id, timer);
          }

          return id;
        },

        removeNotification: (id: string) => {
          toast.dismiss(id);
          set(state => ({
            notifications: state.notifications.filter(n => n.id !== id),
          }));

          const timer = notificationTimers.get(id);
          if (timer) {
            clearTimeout(timer);
            notificationTimers.delete(id);
          }
        },

        clearNotifications: () => {
          notificationTimers.forEach(timer => clearTimeout(timer));
          notificationTimers.clear();
          toast.dismiss();
          set({ notifications: [] });
        },

        showModal: (modal) => {
          const id = `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const newModal: Modal = {
            ...modal,
            id,
            size: modal.size || 'md',
            closable: modal.closable !== false,
          };

          set(state => ({
            modals: [...state.modals, newModal],
          }));

          return id;
        },

        hideModal: (id: string) => {
          set(state => ({
            modals: state.modals.filter(m => m.id !== id),
          }));
        },

        hideAllModals: () => {
          set({ modals: [] });
        },

        startLoading: (message: string, id?: string) => {
          const loadingId = id || `loading_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const newLoading: LoadingState = {
            id: loadingId,
            message,
          };

          set(state => ({
            loadingStates: [...state.loadingStates.filter(l => l.id !== loadingId), newLoading],
          }));

          return loadingId;
        },

        updateLoadingProgress: (id: string, progress: number, message?: string) => {
          set(state => ({
            loadingStates: state.loadingStates.map(loading =>
              loading.id === id
                ? { ...loading, progress: Math.max(0, Math.min(100, progress)), ...(message && { message }) }
                : loading
            ),
          }));
        },

        stopLoading: (id: string) => {
          set(state => ({
            loadingStates: state.loadingStates.filter(l => l.id !== id),
          }));
        },

        stopAllLoading: () => {
          set({ loadingStates: [] });
        },

        enterFullscreen: () => {
          if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
          }
          set({ isFullscreen: true });
        },

        exitFullscreen: () => {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          }
          set({ isFullscreen: false });
        },

        toggleFullscreen: () => {
          const { isFullscreen } = get();
          if (isFullscreen) {
            get().exitFullscreen();
          } else {
            get().enterFullscreen();
          }
        },

        setKeyboardShortcuts: (enabled: boolean) => {
          set({ keyboardShortcutsEnabled: enabled });
        },

        setAnimations: (enabled: boolean) => {
          set({ animationsEnabled: enabled });
          
          // Apply to document for CSS animations
          if (enabled) {
            document.documentElement.classList.remove('no-animations');
          } else {
            document.documentElement.classList.add('no-animations');
          }
        },

        setSound: (enabled: boolean) => {
          set({ soundEnabled: enabled });
        },

        setCompactMode: (enabled: boolean) => {
          set({ compactMode: enabled });
          
          // Apply to document
          if (enabled) {
            document.documentElement.classList.add('compact-mode');
          } else {
            document.documentElement.classList.remove('compact-mode');
          }
        },

        setFontSize: (size: 'small' | 'medium' | 'large') => {
          set({ fontSize: size });
          
          // Apply to document
          document.documentElement.classList.remove('font-small', 'font-medium', 'font-large');
          document.documentElement.classList.add(`font-${size}`);
        },

        setLanguage: (language: string) => {
          set({ language });
          
          // Apply to document
          document.documentElement.lang = language;
        },

        setColorMode: (mode: ColorMode) => {
          const resolvedTheme = applyDocumentTheme(mode);
          set({ colorMode: mode, resolvedTheme });
        },

        syncResolvedTheme: () => {
          const { colorMode } = get();
          const resolvedTheme = applyDocumentTheme(colorMode);
          set({ resolvedTheme });
        },

        // Convenience methods for common notifications
        showSuccessNotification: (title: string, message: string) => {
          get().addNotification({
            type: 'success',
            title,
            message,
            duration: 4000,
          });
        },

        showErrorNotification: (title: string, message: string) => {
          get().addNotification({
            type: 'error',
            title,
            message,
            duration: 8000,
          });
        },

        showWarningNotification: (title: string, message: string) => {
          get().addNotification({
            type: 'warning',
            title,
            message,
            duration: 6000,
          });
        },

        showInfoNotification: (title: string, message: string) => {
          get().addNotification({
            type: 'info',
            title,
            message,
            duration: 5000,
          });
        },

        showConfirmModal: (title: string, content: string, onConfirm: () => void) => {
          return get().showModal({
            type: 'confirm',
            title,
            content,
            onConfirm,
            confirmText: 'Confirm',
            cancelText: 'Cancel',
          });
        },

        showNotification: (notification: { type: NotificationType; title: string; message: string; duration?: number }) => {
          return get().addNotification({
            type: notification.type,
            title: notification.title,
            message: notification.message,
            duration: notification.duration || 5000,
          });
        },
      }),
      {
        name: 'quizsecure-ui',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          keyboardShortcutsEnabled: state.keyboardShortcutsEnabled,
          animationsEnabled: state.animationsEnabled,
          soundEnabled: state.soundEnabled,
          compactMode: state.compactMode,
          fontSize: state.fontSize,
          language: state.language,
          colorMode: state.colorMode,
        }),
        onRehydrateStorage: () => (state) => {
          if (!state) return;
          const resolvedTheme = applyDocumentTheme(state.colorMode);
          useUIStore.setState({ resolvedTheme });
        },
      }
    ),
    { name: 'UIStore' }
  )
);

// Initialize persisted interface preferences
export const initUIStore = () => {
  const { setAnimations, setCompactMode, setFontSize, syncResolvedTheme, colorMode } =
    useUIStore.getState();

  const state = useUIStore.getState();
  setAnimations(state.animationsEnabled);
  setCompactMode(state.compactMode);
  setFontSize(state.fontSize);
  syncResolvedTheme();

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemThemeChange = () => {
    if (useUIStore.getState().colorMode === 'system') {
      useUIStore.getState().syncResolvedTheme();
    }
  };
  media.addEventListener('change', onSystemThemeChange);

  // Listen for fullscreen changes
  const handleFullscreenChange = () => {
    const isFullscreen = Boolean(document.fullscreenElement);
    useUIStore.setState({ isFullscreen });
  };

  document.addEventListener('fullscreenchange', handleFullscreenChange);

  // Ensure mode from persist is applied once rehydrated
  void colorMode;

  return () => {
    media.removeEventListener('change', onSystemThemeChange);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
  };
};

// Keyboard shortcuts manager
export const setupKeyboardShortcuts = () => {
  const handleKeyDown = (event: KeyboardEvent) => {
    const { keyboardShortcutsEnabled } = useUIStore.getState();
    
    if (!keyboardShortcutsEnabled) return;
    
    // Global shortcuts
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'k':
          event.preventDefault();
          // Toggle command palette or search
          break;
        case 'b':
          event.preventDefault();
          useUIStore.getState().toggleSidebar();
          break;
        case 'f':
          event.preventDefault();
          useUIStore.getState().toggleFullscreen();
          break;
      }
    }
    
    // Escape key to close modals/notifications
    if (event.key === 'Escape') {
      const { modals, notifications } = useUIStore.getState();
      
      if (modals.length > 0) {
        const topModal = modals[modals.length - 1];
        if (topModal.closable !== false) {
          useUIStore.getState().hideModal(topModal.id);
        }
      } else if (notifications.length > 0) {
        useUIStore.getState().clearNotifications();
      }
    }
  };
  
  document.addEventListener('keydown', handleKeyDown);
  
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};
